import React, { useState, useEffect } from 'react';

const LegalPage = () => {
  const [activeTab, setActiveTab] = useState('privacy');

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  return (
    <div className="legal-page">
      <style>{`
        .legal-page {
          min-height: 100vh;
          background-color: #f8fafc;
          padding: 40px 20px;
          font-family: Inter, system-ui, -apple-system, sans-serif;
          color: #334155;
        }

        .legal-container {
          max-width: 860px;
          margin: 0 auto;
          background: white;
          border-radius: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }

        .legal-header {
          background-color: #0f172a;
          color: white;
          padding: 48px 40px;
        }

        .legal-header h1 {
          margin: 0;
          font-size: 2.2rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .legal-date {
          color: #94a3b8;
          font-size: 0.95rem;
          margin-top: 8px;
        }

        .legal-intro {
          color: #cbd5e1;
          margin-top: 16px;
          line-height: 1.6;
          font-size: 1.05rem;
          max-width: 600px;
        }

        .tab-nav {
          display: flex;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }

        .tab-btn {
          flex: 1;
          padding: 20px;
          background: none;
          border: none;
          font-size: 1.05rem;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 3px solid transparent;
        }

        .tab-btn:hover {
          color: #0f172a;
          background: #f1f5f9;
        }

        .tab-btn.active {
          color: #4f46e5;
          border-bottom-color: #4f46e5;
          background: white;
        }

        .legal-content {
          padding: 40px;
        }

        .legal-section {
          margin-bottom: 40px;
          animation: fadeIn 0.4s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .legal-section h2 {
          color: #0f172a;
          font-size: 1.4rem;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f1f5f9;
        }

        .legal-section p {
          line-height: 1.7;
          margin-bottom: 12px;
        }

        .legal-section ul {
          padding-left: 24px;
          line-height: 1.7;
        }

        .legal-section li {
          margin-bottom: 8px;
        }

        .legal-section strong {
          color: #0f172a;
        }

        @media (max-width: 640px) {
          .legal-page { padding: 16px; }
          .legal-header { padding: 32px 24px; }
          .tab-btn { padding: 16px 12px; font-size: 0.95rem; }
          .legal-content { padding: 24px; }
        }
      `}</style>

      <div className="legal-container">
        {/* Header */}
        <div className="legal-header">
          <h1>Legal & Privacy</h1>
          <div className="legal-date">Effective Date: June 17, 2026</div>
          <p className="legal-intro">
            Welcome to the Utkarsh Abhyasika app. We are committed to protecting your privacy and providing a secure, productive environment for your studies.
          </p>
        </div>

        {/* Tabs */}
        <div className="tab-nav">
          <button
            className={`tab-btn ${activeTab === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy Policy
          </button>
          <button
            className={`tab-btn ${activeTab === 'terms' ? 'active' : ''}`}
            onClick={() => setActiveTab('terms')}
          >
            Terms & Conditions
          </button>
        </div>

        {/* Content */}
        <div className="legal-content">
          {activeTab === 'privacy' && (
            <div className="legal-section">
              <h2>1. Information We Collect</h2>
              <p>To provide a secure and efficient library environment, we collect:</p>
              <ul>
                <li><strong>Identity Data:</strong> Full name, date of birth, and gender.</li>
                <li><strong>Contact Data:</strong> Email address and phone number.</li>
                <li><strong>Sensitive National ID:</strong> Photographs of Aadhaar cards (strictly for institutional membership verification).</li>
                <li><strong>Educational Data:</strong> Target exam or curriculum.</li>
                <li><strong>Vehicle Data:</strong> Registration numbers for parking management.</li>
                <li><strong>Images:</strong> Profile photographs and uploaded verification documents.</li>
              </ul>

              <h2 style={{ marginTop: '40px' }}>2. How We Use Your Data</h2>
              <p>Your data is never sold. We utilize it exclusively to:</p>
              <ul>
                <li>Verify identity for physical admission.</li>
                <li>Manage digital seat allocation and track occupancy.</li>
                <li>Deliver institutional notices and emergency alerts via Firebase Cloud Messaging (FCM).</li>
                <li>Process and manually verify fee payments via submitted transaction screenshots.</li>
              </ul>

              <h2 style={{ marginTop: '40px' }}>3. Third-Party Service Providers</h2>
              <p>We partner with industry-standard third-party services:</p>
              <ul>
                <li><strong>Firebase (Google):</strong> For push notifications and secure device tokens.</li>
                <li><strong>Render:</strong> Hosts our secure backend API and student database.</li>
                <li><strong>uCrop:</strong> Local device processing for profile and document photos.</li>
                <li><strong>External UPI Applications:</strong> Interacts with installed UPI apps to facilitate external fee payments.</li>
              </ul>

              <h2 style={{ marginTop: '40px' }}>4. Data Security</h2>
              <ul>
                <li><strong>Encrypted Transmission:</strong> All data is transmitted over secure HTTPS (TLS 1.2+).</li>
                <li><strong>Secure Storage:</strong> Sensitive identifiers (Aadhaar) are stored on encrypted servers and never shared with marketing agencies.</li>
              </ul>
            </div>
          )}

          {activeTab === 'terms' && (
            <div className="legal-section">
              <h2>1. Financial Policy</h2>
              <ul>
                <li><strong>Strictly Non-Refundable:</strong> Admission fees, security deposits, and monthly fees are non-refundable under any circumstances.</li>
                <li><strong>Manual Verification:</strong> Submitting a payment screenshot does not grant instant approval. All payments are subject to manual verification by administration.</li>
              </ul>

              <h2 style={{ marginTop: '40px' }}>2. User Conduct & Library Rules</h2>
              <ul>
                <li><strong>Rules Compliance:</strong> Users must strictly adhere to the physical library rules outlined in the app.</li>
                <li><strong>Right to Terminate:</strong> Administration reserves the right to revoke access, cancel seat allocation, and terminate memberships for rule violations without prior notice or refund.</li>
              </ul>

              <h2 style={{ marginTop: '40px' }}>3. Institutional Communication</h2>
              <ul>
                <li><strong>Digital Notices:</strong> Users agree to receive official notices and rule changes electronically.</li>
                <li><strong>Polls and Feedback:</strong> In-app polls are for feedback purposes; final decisions rest with management.</li>
              </ul>

              <h2 style={{ marginTop: '40px' }}>4. Account Responsibility</h2>
              <ul>
                <li><strong>Security:</strong> You are responsible for maintaining the confidentiality of your account password.</li>
                <li><strong>Accuracy of Information:</strong> Providing fraudulent details (e.g., fake Aadhaar info or forged payment screenshots) will lead to immediate termination and a permanent ban.</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegalPage;